import { StateReportOptions } from '../data-models/state-report-options.model';
import { CreateStateReportRequest } from '../request-models/create-state-report-request.model';
import { isRecord } from './is-record';
import { nullableBoolean, omitUndefined } from './wire-value-utils';

/**
 * Default options for state reports (all enabled)
 */
export const DEFAULT_STATE_REPORT_OPTIONS: StateReportOptions = {
  includeChemicalDetails: true,
  includeApplicationRates: true,
  includeFieldMaps: true,
  includeWeatherConditions: true
};

/**
 * Available state report types
 */
export const STATE_REPORT_TYPE_VALUES = [
  { value: 'all', label: 'All Applications' },
  { value: 'pesticide', label: 'Pesticide Applications' },
  { value: 'fertilizer', label: 'Fertilizer Applications' },
  { value: 'seeding', label: 'Seeding Applications' },
  { value: 'other', label: 'Other Applications' }
] as const;

/**
 * Reads a report's `options` block: `undefined` when the hub didn't send it, `null` when it sent `null`.
 * A flag the hub left out reads as `false` (the block is always sent whole).
 */
export function toStateReportOptions(value: unknown): StateReportOptions | null | undefined {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    return undefined;
  }
  return {
    includeChemicalDetails: nullableBoolean(value['includeChemicalDetails']) ?? false,
    includeApplicationRates: nullableBoolean(value['includeApplicationRates']) ?? false,
    includeFieldMaps: nullableBoolean(value['includeFieldMaps']) ?? false,
    includeWeatherConditions: nullableBoolean(value['includeWeatherConditions']) ?? false,
  };
}

/** The create body: only the allowlisted keys that are set. Never send a `StateReport`. */
export function toCreateStateReportRequest(request: CreateStateReportRequest): CreateStateReportRequest {
  const options = request.options;
  return omitUndefined({
    name: request.name,
    state: request.state,
    startDate: request.startDate,
    endDate: request.endDate,
    reportType: request.reportType,
    options: {
      includeChemicalDetails: options.includeChemicalDetails,
      includeApplicationRates: options.includeApplicationRates,
      includeFieldMaps: options.includeFieldMaps,
      includeWeatherConditions: options.includeWeatherConditions,
    },
  });
}
